import { keepPreviousData, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useClientStore } from '@/client/stores/client';

/** Product models (`entity.model = 1`): the catalogue sellers clone listings from. */
export const BASE = '/api/admin/stores/catalog';

export interface CatalogueModel {
  eid: number;
  ffid: string | null;
  name: string;
  name_marketing: string | null;
  etid: number;
  category: string | null;
  gtin: string | null;
  source: string | null;
  private: number;
  deleted: number;
  tester: number;
  oid: number | null;
  uid: number | null;
  usage: number;
  photos: number;
  date_added: string | null;
  image: string | null;
}

export interface BrowseResponse {
  total: number;
  page: number;
  per_page: number;
  models: CatalogueModel[];
}

export interface WeekPoint {
  week: string | null;
  count: number;
}

export interface CatalogueStats {
  total: number;
  public: number;
  private: number;
  retired: number;
  with_photos: number;
  without_photos: number;
  listed_by_sellers: number;
  by_source: { source: string; count: number }[];
  by_category: { etid: number; category: string | null; count: number; with_photos: number }[];
  added_per_week: WeekPoint[];
  listings_per_week: WeekPoint[];
  weeks: number;
}

export interface Category {
  etid: number;
  name: string;
  path: string;
  models: number;
}

export interface ModelAttribute {
  eaid: number;
  eatid: number;
  content: string | null;
  content_json: unknown;
  name: string;
  name_system: string | null;
  unit: string | null;
}

export interface ModelImage {
  asid: number;
  url: string | null;
  public_url: string | null;
  path: string | null;
  order_by: number | null;
  default: number;
  description: string | null;
  mime_type: string | null;
  date_added: string | null;
}

export interface ModelListing {
  eid: number;
  name: string | null;
  uid: number | null;
  oid: number | null;
  status: string | null;
  stores: (number | string)[];
  date_added: string | null;
}

export interface ModelDetail {
  eid: number;
  ffid: string | null;
  name: string | null;
  name_marketing: string | null;
  description: string | null;
  description_short: string | null;
  gtin: string | null;
  etid: number;
  category: string | null;
  source: string | null;
  private: number | null;
  deleted: number | null;
  tester: number | null;
  oid: number | null;
  uid: number | null;
  date_added: string | null;
  date_modified: string | null;
  attributes: ModelAttribute[];
  images: ModelImage[];
  listings: ModelListing[];
  listings_total: number;
}

export interface AttributeType {
  eatid: number;
  name: string | null;
  name_system: string | null;
  unit: string | null;
  options: unknown;
  protected: number;
  used_in_category: number;
}

export interface BrowseFilters {
  q: string;
  etid: string;
  source: string;
  photos: string;
  visibility: string;
  status: string;
  kind: string;
  sort: string;
  page: number;
  perPage: number;
}

export const DEFAULT_FILTERS: BrowseFilters = {
  q: '',
  etid: '',
  source: '',
  photos: '',
  visibility: 'all',
  status: 'active',
  kind: '',
  sort: 'recent',
  page: 1,
  perPage: 50,
};

export type BulkAction = 'retire' | 'restore' | 'make_public' | 'make_private' | 'set_category';

/** The API's error text, or a fallback. ff-api answers `{error, error_description}`. */
export const errorText = (err: unknown, fallback: string) => {
  if (err && typeof err === 'object') {
    const e = err as { error_description?: string; message?: string; response?: { data?: any } };
    return e.response?.data?.error_description || e.error_description || e.message || fallback;
  }
  return fallback;
};

const failure = (res: unknown): string | null => {
  if (res && typeof res === 'object' && 'error' in (res as object)) {
    const r = res as { error?: string; error_description?: string };
    return r.error_description || r.error || 'The request failed.';
  }
  return null;
};

type Method = 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';

const useRequest = () => useClientStore((s) => s.request);

/** Call the API and throw on an `{error}` body, so callers handle one failure path. */
export const useCall = () => {
  const request = useRequest();
  return async <T,>(path: string, method: Method = 'GET', body?: unknown): Promise<T> => {
    const res = await request<T>(`${BASE}${path}`, method, body as any);
    const msg = failure(res);
    if (msg) throw new Error(msg);
    return res as T;
  };
};

const browsePath = (f: BrowseFilters) => {
  const p = new URLSearchParams({
    page: String(f.page),
    per_page: String(f.perPage),
    sort: f.sort,
    status: f.status,
    visibility: f.visibility,
  });
  (['q', 'etid', 'source', 'photos', 'kind'] as const).forEach((k) => {
    if (f[k]) p.set(k, f[k]);
  });
  return `/browse?${p.toString()}`;
};

export const useBrowse = (f: BrowseFilters) => {
  const call = useCall();
  return useQuery({
    queryKey: ['product-catalogue', 'browse', f],
    queryFn: () => call<BrowseResponse>(browsePath(f)),
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
};

export const useStats = () => {
  const call = useCall();
  return useQuery({
    queryKey: ['product-catalogue', 'stats'],
    queryFn: () => call<CatalogueStats>('/stats?weeks=12'),
    staleTime: 60_000,
  });
};

export const useCategories = () => {
  const call = useCall();
  return useQuery({
    queryKey: ['product-catalogue', 'categories'],
    queryFn: () => call<{ categories: Category[] }>('/categories'),
    staleTime: 10 * 60_000,
    select: (d) => d.categories,
  });
};

export const useModel = (eid: number | null) => {
  const call = useCall();
  return useQuery({
    queryKey: ['product-catalogue', 'model', eid],
    queryFn: () => call<ModelDetail>(`/${eid}`),
    enabled: eid != null,
    staleTime: 10_000,
  });
};

export const useAttributeTypes = (etid: number | null, q: string) => {
  const call = useCall();
  return useQuery({
    queryKey: ['product-catalogue', 'attribute-types', etid, q],
    queryFn: () =>
      call<{ types: AttributeType[] }>(
        `/attribute-types?limit=40${etid ? `&etid=${etid}` : ''}${q ? `&q=${encodeURIComponent(q)}` : ''}`
      ),
    staleTime: 5 * 60_000,
    select: (d) => d.types,
  });
};

/**
 * A write against the catalogue: toasts the outcome and refreshes the table,
 * the stats and the open model, which every write can change.
 */
export const useCatalogueWrite = <V,>(
  run: (call: ReturnType<typeof useCall>, vars: V) => Promise<unknown>,
  success: string | ((vars: V, res: any) => string)
) => {
  const call = useCall();
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (vars: V) => run(call, vars),
    onSuccess: (res, vars) => {
      toast.success(typeof success === 'function' ? success(vars, res) : success);
      qc.invalidateQueries({ queryKey: ['product-catalogue'] });
    },
    onError: (err) => toast.error(errorText(err, 'The change was not saved.')),
  });
};
